import { createClient, Entry, EntrySkeletonType } from "contentful"
import Settings from "../../utils/CarrotConfig"
import ContentTypes, { EntryWithoutUnresolvableLinks } from "../../utils/ContentTypes"
import { nestedRefs } from "./utils/contentfulClientUtils"
import { reportError, reportErrorMessage } from "#/utils/ErrorReporting"
import { getContentfulLocale } from "./utils/contentfulLocaleUtils"
import {
  // TypeCalendlyLinkSkeleton is the TimeTap link type, auto-generated from Contentful
  TypeCalendlyLinkSkeleton,
  TypeCountrySkeleton,
  TypeExpertBioSkeleton,
  TypePageGuidanceSkeleton,
  TypeTalkToCarrotCountryV2Skeleton,
  TypeTalkToCarrotLanguageSkeleton,
  TypeTileDisplaySkeleton,
  TypeTilePageSkeleton,
  TypeTileSkeleton
} from "#/types/contentful"

enum StaticAssetID {
  HOME_PAGE_BENEFIT_HIGHLIGHTS_CONTAINER_ID = "H7yuTmAsG92qt9Pqf46Gn",
  TALK_TO_CARROT_LOCATION_GUIDANCE = "3ByERJy0buFOMf6f9BU6RQ",
  TALK_TO_CARROT_LANGUAGE_GUIDANCE = "1m5qiXAayKU40WZ2Ega5nE",
  TALK_TO_CARROT_NEED_HELP_GUIDANCE = "6HZUR0v8YJYCGeFG5ncCtm",
  TALK_TO_CARROT_PROVIDER_ELIGIBILITY = "3Tnl2nccRTKkk4oMC4ZmxB",
  TALK_TO_CARROT_REFRESH_CARROT_PLAN = "4oPn08X5v4mH61LsrqKT3H",
  TALK_TO_CARROT_GET_YOUR_CARROT_PLAN = "1n7FzTcMpdeJmMYir1FvHB",
  TALK_TO_CARROT_DELETE_ACCOUNT_ENGLISH = "4jq2bKHVWVg85SMRK6XEGd",
  TALK_TO_CARROT_EXPERT_PAGE_GUIDANCE = "1izopz7aGxJqPRfYi8pnNW",
  TALK_TO_CARROT_CARE_NAVIGATION_GUIDANCE = "d6DOvsVjsS8jwAi5WNxa3",
  TALK_TO_CARROT_CARE_NAV_DISPLAY = "2VsaJ0QmxiNMWStiSPDLWo",
  TALK_TO_CARROT_SPECIALIST_DISPLAY = "4HDGYmodsvm6zNiKXcYSQn",
  TALK_TO_CARROT_DISABLED_SPECIALIST_DISPLAY = "2ODqMLjncY3RpmpRv59KoU",
  TALK_TO_CARROT_SEND_A_MESSAGE_GUIDANCE = "5dRHHZv3CWk8jslQK3SOyS",
  TALK_TO_CARROT_SEND_A_MESSAGE_DISPLAY = "7pgPTNlFt1WrMY2Au6mizO",
  TALK_TO_CARRROT_PHONE_SUPPORT_DISPLAY = "6iXSWydnuzS4EfpfMQ1YrL",
  TALK_TO_CARROT_CALL_US_DISPLAY = "4XYXSfc7nZMR5kHqgkga6",
  TALK_TO_CARROT_BENEFIT_CHAT_LANGUAGE_ENGLISH = "1mkdRSUsXWTtw9Y5gzgvP2",
  TALK_TO_CARROT_SALES_DEMO_TIMETAP_LINK = "6RMSiAjB3mFZh6FkZBKR7Z",
  BLUEBERRY_PEDIATRICS_PAGE = "5rAdkk7JXNovv98CC423Y4",
  MEDICAL_PRIME_PAGE = "7kvAgOfBka8XmKntpvBFy4",
  ORIGIN_PAGE = "1YVZwvVrinIzm4t6Z0L86D"
}

export const TaxonomyConcept = {
  CARROT_ACADEMY: "carrotAcademy"
}

const getContentfulClient = () =>
  createClient({
    space: Settings.CONTENTFUL_SPACE_ID,
    accessToken: Settings.CONTENTFUL_ACCESS_TOKEN,
    host: Settings.CONTENTFUL_PREVIEW_MODE === "true" ? "preview.contentful.com" : "cdn.contentful.com",
    ...(Settings.CONTENTFUL_ENVIRONMENT && { environment: Settings.CONTENTFUL_ENVIRONMENT })
  })

const getTalkToCarrotLocations = async () => {
  const client = getContentfulClient()
  const { items, total, limit } = await client.getEntries<TypeTalkToCarrotCountryV2Skeleton>({
    content_type: ContentTypes.TALK_TO_CARROT_COUNTRY_V2,
    include: 1,
    limit: 300
  })
  if (total > limit) {
    reportErrorMessage(
      `Contentful request containted ${total} items exceeded the request limit of ${limit}. Please check the request and adjust the limit accordingly.`
    )
  }
  return items
}

const getCountries = async () => {
  const client = getContentfulClient()
  const { items, total, limit } = await client.getEntries<TypeCountrySkeleton>({
    content_type: ContentTypes.COUNTRY,
    include: 1,
    limit: 300
  })
  if (total > limit) {
    reportErrorMessage(
      `Contentful request containted ${total} items which exceeded the request limit of ${limit}. Please check the request and adjust the limit accordingly.`
    )
  }
  return items
}

const getTalkToCarrotLanguages = async () => {
  const client = getContentfulClient()
  const { items } = await client.withoutUnresolvableLinks.getEntries<TypeTalkToCarrotLanguageSkeleton>({
    content_type: ContentTypes.TALK_TO_CARROT_LANGUAGE,
    include: 10
  })
  return items
}

const getEntryById = async (id: string, locale: string): Promise<Entry<EntrySkeletonType>> => {
  const client = getContentfulClient()
  const contentfulLocale = getContentfulLocale(locale)
  try {
    return await client.getEntry(id, { include: 10, locale: contentfulLocale })
  } catch (err) {
    if (err && err.sys) {
      const {
        sys: { id: errorId }
      } = err
      if (errorId === "NotFound") {
        const errorMsg = `Contentful resource not found for entityId: ${id}, this may just be a work in progress and in a draft state`
        reportErrorMessage(errorMsg)
        throw new ContentfulError(errorMsg)
      }
    }
    reportError(err)
    throw new ContentfulError(err)
  }
}

const getTilePageById = async (id: string, locale: string) => {
  const result = (await getEntryById(id, locale)) as EntryWithoutUnresolvableLinks<TypeTilePageSkeleton>
  await nestedRefs(result, getEntryById)
  return result
}

const getGuidance = async (entryId: StaticAssetID, locale: string) => {
  const item = (await getEntryById(entryId, locale)) as EntryWithoutUnresolvableLinks<TypePageGuidanceSkeleton>
  if (!item) {
    reportErrorMessage(`No Contentful page guidance for entityId: ${entryId}`)
  }
  return item
}

const getTileDisplay = async (entryId: StaticAssetID, locale: string) => {
  const item = (await getEntryById(entryId, locale)) as EntryWithoutUnresolvableLinks<TypeTileDisplaySkeleton>
  if (!item) {
    reportErrorMessage(`No Contentful tile display for entityId: ${entryId}`)
  }
  return item
}

const getExpertBios = async () => {
  const client = getContentfulClient()
  const { items, total, limit } = await client.getEntries<TypeExpertBioSkeleton>({
    content_type: ContentTypes.EXPERT_BIO,
    include: 1,
    limit: 300
  })
  if (total > limit) {
    reportErrorMessage(
      `Contentful request containted ${total} items exceeded the request limit of ${limit}. Please check the request and adjust the limit accordingly.`
    )
  }
  return items
}

const getParentTileEntries = async (
  currentEntryId: string
): Promise<Entry<TypeTileSkeleton, "WITHOUT_UNRESOLVABLE_LINKS", string>[]> => {
  const client = getContentfulClient()

  const response = await client.getEntries<TypeTileSkeleton>({
    content_type: ContentTypes.TILE,
    links_to_entry: currentEntryId
  })

  if (response.items.length > 0) {
    const parentEntryIds: string[] = response.items.map((item) => item.sys.id)

    const parentEntriesResponse = await client.getEntries<TypeTileSkeleton>({
      content_type: ContentTypes.TILE,
      "sys.id[in]": parentEntryIds,
      include: 2
    })

    return parentEntriesResponse.items as Entry<TypeTileSkeleton, "WITHOUT_UNRESOLVABLE_LINKS", string>[]
  }

  return []
}

const getTileEntries = async (entryIds: string[]): Promise<Entry<TypeTileSkeleton>[]> => {
  if (!entryIds.length) return []
  const client = getContentfulClient()
  const { items } = await client.getEntries<TypeTileSkeleton>({
    content_type: "tile",
    "sys.id[in]": entryIds,
    include: 2
  })
  return items
}

const getTimeTapLinkByReasonIdAndLocationId = async (
  reasonId: number,
  locationId: number
): Promise<EntryWithoutUnresolvableLinks<TypeCalendlyLinkSkeleton>> => {
  const client = getContentfulClient()
  const { items } = await client.getEntries<TypeCalendlyLinkSkeleton>({
    content_type: ContentTypes.TIMETAP_LINK,
    "fields.reasonId": reasonId,
    "fields.locationId": locationId
  })
  return items[0] as EntryWithoutUnresolvableLinks<TypeCalendlyLinkSkeleton>
}

const getTalkToCarrotLanguageGuidance = async (locale: string) =>
  getGuidance(StaticAssetID.TALK_TO_CARROT_LANGUAGE_GUIDANCE, locale)

const getTalkToCarrotLocationGuidance = async (locale: string) =>
  getGuidance(StaticAssetID.TALK_TO_CARROT_LOCATION_GUIDANCE, locale)

const getTalkToCarrotNeedHelpGuidance = async (locale: string) =>
  getGuidance(StaticAssetID.TALK_TO_CARROT_NEED_HELP_GUIDANCE, locale)

const getTalkToCarrotProviderEligibilityGuidance = async (locale: string) =>
  getGuidance(StaticAssetID.TALK_TO_CARROT_PROVIDER_ELIGIBILITY, locale)

const getTalkToCarrotDeleteAccountGuidance = async (locale: string) =>
  getGuidance(StaticAssetID.TALK_TO_CARROT_DELETE_ACCOUNT_ENGLISH, locale)

const getTalkToCarrotExpertGuidance = async (locale: string) =>
  getGuidance(StaticAssetID.TALK_TO_CARROT_EXPERT_PAGE_GUIDANCE, locale)

const getTalkToCarrotCareNavigationGuidance = async (locale: string) =>
  getGuidance(StaticAssetID.TALK_TO_CARROT_CARE_NAVIGATION_GUIDANCE, locale)

const getCareNavTileDisplay = async (locale: string) =>
  getTileDisplay(StaticAssetID.TALK_TO_CARROT_CARE_NAV_DISPLAY, locale)

const getSpecialistTileDisplay = async (locale: string) =>
  getTileDisplay(StaticAssetID.TALK_TO_CARROT_SPECIALIST_DISPLAY, locale)

const getDisabledSpecialistTileDisplay = async (locale: string) =>
  getTileDisplay(StaticAssetID.TALK_TO_CARROT_DISABLED_SPECIALIST_DISPLAY, locale)

const getTalkToCarrotSendMessageGuidance = async (locale: string) =>
  getGuidance(StaticAssetID.TALK_TO_CARROT_SEND_A_MESSAGE_GUIDANCE, locale)

const getTalkToCarrotSendMessageDisplay = async (locale: string) =>
  getTileDisplay(StaticAssetID.TALK_TO_CARROT_SEND_A_MESSAGE_DISPLAY, locale)

const getTalkToCarrotPhoneSupportDisplay = async (locale: string) =>
  getTileDisplay(StaticAssetID.TALK_TO_CARRROT_PHONE_SUPPORT_DISPLAY, locale)

const getCallUsDisplay = async (locale: string) => getTileDisplay(StaticAssetID.TALK_TO_CARROT_CALL_US_DISPLAY, locale)

export {
  StaticAssetID,
  getContentfulClient,
  getEntryById,
  getTalkToCarrotLocations,
  getTalkToCarrotLocationGuidance,
  getTilePageById,
  getTalkToCarrotNeedHelpGuidance,
  getTalkToCarrotProviderEligibilityGuidance,
  getTalkToCarrotLanguageGuidance,
  getCountries,
  getTalkToCarrotLanguages,
  getTalkToCarrotDeleteAccountGuidance,
  getTalkToCarrotExpertGuidance,
  getTalkToCarrotCareNavigationGuidance,
  getCareNavTileDisplay,
  getSpecialistTileDisplay,
  getDisabledSpecialistTileDisplay,
  getTalkToCarrotSendMessageGuidance,
  getTalkToCarrotSendMessageDisplay,
  getTalkToCarrotPhoneSupportDisplay,
  getCallUsDisplay,
  getExpertBios,
  getParentTileEntries,
  getTileEntries,
  getTimeTapLinkByReasonIdAndLocationId
}

class ContentfulError extends Error {
  constructor(message: string) {
    super(message)
    Object.setPrototypeOf(this, ContentfulError.prototype)
  }
}
